import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { useFeature } from 'flagged'

import MobileNavModal from 'shared/mobile-nav-modal'
import StoreSearchButton from 'store/components/search-button'
import CartSummary from 'cart/components/cart-summary'
import classNames from 'classnames'
import { camelCase, mapKeys } from 'lodash'
import {
  absoluteUrl,
  faqPath,
  indexPath,
  loginPathWithRedirect,
  newRegistryPath,
  registryHomePath,
} from 'lib/urls'
import { initializePurchasingStore } from 'cart/lib'
import {
  ACTIVE_NAV_SECTION_EVENT,
  HIDE_MOBILE_NAV_MODAL_EVENT,
} from 'shared/constants'
import { track, useTracking, withContextualizedTracking } from 'lib/analytics'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { setCurrentUser } from 'shared/utils/setCurrentUser/setCurrentUser'
import css from './global-nav.scss'
import AccountMenu from './components/AccountMenu'
import ToggleNav from './components/ToggleNav'
import SiteNavSection from './components/SiteNavSection'
import withCurrentUser from 'shared/HOC/withCurrentUser'
import MessageCenterMenu from 'components/global-nav/components/MessageCenterMenu'
import SearchBar from './components/SearchBar/DesktopSearchBar'
import withReactQueryClientProvider from 'shared/HOC/withReactQueryClientProvider'
import MobileSearchBar from './components/SearchBar/MobileSearchBar'

const chooseEventLocation = (activeNavItem) => {
  switch (activeNavItem) {
    case 'Home':
      return track.EventLocation.HOME_PAGE
    case 'Registry':
      return track.EventLocation.REGISTRY
    case 'Holiday':
      return track.EventLocation.NONE
    case 'Guides':
      return track.EventLocation.GUIDE
    case 'Store':
      return track.EventLocation.STORE
    default:
      return track.EventLocation.NONE
  }
}

@withContextualizedTracking((props) => {
  // use to track cart opened eventLocation
  const { activeNavItem } = props
  const eventLocation = chooseEventLocation(activeNavItem)
  return { eventLocation }
})
class GlobalNav extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      activeNavItem: props.activeNavItem,
      sideNavVisible: false,
    }
    this.handleToggleNav = this.handleToggleNav.bind(this)
  }

  componentDidMount() {
    PubSub.subscribe(HIDE_MOBILE_NAV_MODAL_EVENT, () => {
      this.setState({ sideNavVisible: false })
    })
    PubSub.subscribe(ACTIVE_NAV_SECTION_EVENT, (event) => {
      this.setState({ activeNavItem: event })
    })
    PubSub.subscribe('switchNavState', (user) => {
      const formattedUser = mapKeys(user, (_, key) => camelCase(key))
      setCurrentUser(formattedUser)
    })
  }

  handleToggleNav() {
    this.setState({
      sideNavVisible: !this.state.sideNavVisible,
    })
  }

  render() {
    const {
      currentCart,
      currentUser,
      hideCartSummary,
      isRegistryGuestView,
      showMinimalView,
      showAcquisitionView,
      isLoggedIn,
      ...restOfProps
    } = this.props

    const { activeNavItem, sideNavVisible } = this.state

    return (
      <StatelessGlobalNav
        activeNavItem={activeNavItem}
        currentCart={currentCart}
        currentUser={currentUser}
        hideCartSummary={hideCartSummary}
        isRegistryGuestView={isRegistryGuestView}
        showMinimalView={showMinimalView}
        showAcquisitionView={showAcquisitionView}
        isLoggedIn={isLoggedIn}
        sideNavVisible={sideNavVisible}
        handleToggleNav={this.handleToggleNav}
        {...restOfProps}
      />
    )
  }
}

GlobalNav.propTypes = {
  activeNavItem: PropTypes.string,
  currentCart: PropTypes.object,
  showMinimalView: PropTypes.bool,
  userCanViewRegistry: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
}

const StatelessGlobalNav = (props) => {
  const {
    currentCart,
    currentUser,
    hideCartSummary,
    isRegistryGuestView,
    showMinimalView,
    showAcquisitionView,
    isLoggedIn,
    activeNavItem,
    sideNavVisible,
    handleToggleNav,
  } = props

  const c1UnifiedSearchPhase2Enabled = useFeature(
    'c1_unified_search_phase_2_enabled'
  )

  const showGuideSearchButton =
    !c1UnifiedSearchPhase2Enabled && activeNavItem === 'Guides'

  const tracker = useTracking()

  const trackClick = (eventCta) => {
    tracker.trackEvent({
      event: track.navbarClicked,
      eventCta,
      eventLocation: track.EventLocation.WEB_TOP_NAV,
    })
  }

  const eventLocation = chooseEventLocation(activeNavItem)

  return (
    <nav className={classNames(css.globalNav, css.withShadow)}>
      <div
        className={classNames([
          'container',
          css.GlobalNav__Container,
          css.GlobalNav__MessageCenterContainer,
          {
            [css.GlobalNav__SearchContainer]: c1UnifiedSearchPhase2Enabled,
          },
        ])}
      >
        <div className={css.flexRow}>
          <div className={classNames(css.farLeft, css.GlobalNav__Logo)}>
            <Logo trackClick={trackClick} />
          </div>

          <SiteNavSection
            activeNavItem={activeNavItem}
            classNames={css.GlobalNav__SiteNavSection}
            handleToggleNav={handleToggleNav}
            isLoggedIn={isLoggedIn || !!currentUser}
            showAcquisitionView={showAcquisitionView}
            showMinimalView={isRegistryGuestView || showMinimalView}
            trackClick={trackClick}
          />

          <ul className={css.GlobalNav__ActionsNavSection}>
            {c1UnifiedSearchPhase2Enabled && (
              <li className={classNames(css.GlobalNav__Search)}>
                <SearchBar
                  className={css.GlobalNav__SearchBar}
                  eventLocation={eventLocation}
                  isLoggedIn={isLoggedIn || !!currentUser}
                />
              </li>
            )}
            <ActionsNavSection
              cart={currentCart}
              hideCartSummary={hideCartSummary}
              isLoggedIn={isLoggedIn || !!currentUser}
              showAcquisitionView={showAcquisitionView}
              showMinimalView={isRegistryGuestView || showMinimalView}
              showGuideSearchButton={showGuideSearchButton}
              trackClick={trackClick}
            />
          </ul>
          <MobileNavModal
            {...props}
            show={sideNavVisible}
            onHide={handleToggleNav}
          />
        </div>
        {c1UnifiedSearchPhase2Enabled && (
          <MobileSearchBar
            className={css.GlobalNav__MobileSearchBar}
            eventLocation={eventLocation}
            isLoggedIn={isLoggedIn || !!currentUser}
          />
        )}
      </div>
    </nav>
  )
}

const ActionsNavSection = ({
  cart,
  hideCartSummary,
  showMinimalView,
  isLoggedIn,
  showGuideSearchButton,
  showAcquisitionView,
  trackClick,
}) => {
  const reservations = cart.reservations.map((res) =>
    initializePurchasingStore(res)
  )

  return (
    <>
      {!showAcquisitionView && !isLoggedIn && (
        <li className={classNames(css.navLink, css.fullNavOnly)}>
          <a
            target="_blank"
            href={faqPath}
            onClick={() => trackClick('Help')}
            rel=" noopener noreferrer"
          >
            Help
          </a>
        </li>
      )}

      {!showMinimalView &&
        (isLoggedIn ? (
          <li
            className={classNames(
              css.navLink,
              css.GlobalNav__AccountMenu,
              css.fullNavOnly
            )}
          >
            <AccountMenu trackNavbarClicked={trackClick} />
          </li>
        ) : (
          // logged out
          <>
            <li className={classNames(css.fullNavOnly, css.navLink)}>
              <a
                role="button"
                onClick={() => {
                  window.location.href = loginPathWithRedirect(
                    window.location.pathname
                  )
                }}
              >
                Log In
              </a>
            </li>
            <li
              className={classNames(
                css.fullNavOnly,
                css.navLink,
                css.navButtonWrap
              )}
            >
              <a
                href={absoluteUrl(newRegistryPath)}
                onClick={() => trackClick('Sign Up')}
                className={classNames(
                  'btn-pill btn btn-sm btn-info',
                  css.navButton
                )}
              >
                Sign Up
              </a>
            </li>
          </>
        ))}

      {showGuideSearchButton && (
        <li className={classNames(css.navIcnBtn, 'hidden-md', 'visible-xs')}>
          <StoreSearchButton />
        </li>
      )}

      {isLoggedIn && (
        <li
          className={classNames(css.navIcnBtn, css.GlobalNav__MessageCenter)}
          style={{ position: 'relative' }}
        >
          <MessageCenterMenu />
        </li>
      )}

      {!showAcquisitionView && (
        <li className={css.navIcnBtn} style={{ position: 'relative' }}>
          <CartSummary
            cart={cart.cart}
            disableDropdown={hideCartSummary}
            registryUrlSlug={cart.registryUrlSlug}
            reservations={reservations}
          />
        </li>
      )}
    </>
  )
}

ActionsNavSection.propTypes = {
  cart: PropTypes.object,
  showMinimalView: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  showGuideSearchButton: PropTypes.bool,
  showAcquisitionView: PropTypes.bool,
}

const Logo = ({ className, trackClick }) => {
  const [currentUser] = useCurrentUser()

  return (
    <div className={classNames(css.logo, className)}>
      <a
        href={
          currentUser ? absoluteUrl(registryHomePath) : absoluteUrl(indexPath)
        }
        onClick={() => trackClick('Logo')}
      >
        Babylist - Best Baby Registry
      </a>
    </div>
  )
}

Logo.propTypes = {
  className: PropTypes.string,
  user: PropTypes.object,
}

export default withReactQueryClientProvider(withCurrentUser(GlobalNav))
export { SiteNavSection, ActionsNavSection, Logo, ToggleNav }
